import React, { useState } from "react";
import { api, API } from "../lib/api";
import { Download, Printer, FileSpreadsheet, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

export default function YearEndReport() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const emailReport = async (year) => {
    if (!window.confirm(`Email the ${year} year-end report to the broker now?`)) return;
    setEmailing(true);
    try {
      await api.post(`/reports/year-end/email?year=${year}`);
      toast.success(`Year-end ${year} report emailed to broker`);
    } catch {
      toast.error("Email failed — check backend logs");
    } finally {
      setEmailing(false);
    }
  };

  const downloadReport = async (year) => {
    setDownloading(true);
    const token = localStorage.getItem("core_token");
    try {
      const resp = await fetch(`${API}/reports/year-end?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CORE-Realty-Year-End-${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Year-end ${year} report downloaded`);
    } catch (err) {
      toast.error("Report download failed");
    } finally {
      setDownloading(false);
    }
  };

  const printPage = () => window.print();

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="year-end-actions">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="border border-[#050303] pill-btn hover:bg-[#050303] hover:text-white flex items-center gap-2 text-sm disabled:opacity-50"
            disabled={downloading}
            data-testid="year-end-report-btn"
          >
            <FileSpreadsheet size={15} strokeWidth={1.5} />
            {downloading ? "Preparing…" : "Year-End Report"}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuLabel className="text-xs uppercase tracking-wider">
            Select year
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {YEAR_OPTIONS.map((y) => (
            <DropdownMenuItem
              key={y}
              onClick={() => downloadReport(y)}
              className="cursor-pointer flex items-center gap-2"
              data-testid={`year-end-${y}`}
            >
              <Download size={13} /> Download {y} .xlsx
            </DropdownMenuItem>
          ))}
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs uppercase tracking-wider">
                Email to broker
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {YEAR_OPTIONS.map((y) => (
                <DropdownMenuItem
                  key={`email-${y}`}
                  onClick={() => emailReport(y)}
                  disabled={emailing}
                  className="cursor-pointer flex items-center gap-2"
                  data-testid={`year-end-email-${y}`}
                >
                  <Send size={13} /> {emailing ? "Sending…" : `Email ${y} report`}
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        onClick={printPage}
        className="border border-[#d1cece] pill-btn hover:bg-[#d1cece]/40 flex items-center gap-2 text-sm"
        data-testid="print-pdf-btn"
      >
        <Printer size={15} strokeWidth={1.5} /> Print / PDF
      </button>
    </div>
  );
}
